import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrainTypeField from "@/components/BrainTypeField";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Reusable "Add Coach" or "Add Client" dialog tied to a parent entity.
 * - endpoint: e.g. `/institutes/{id}/coaches` or `/coaches/{id}/users`
 * - kind: "coach" | "user" — for labels and test ids
 */
export default function AddPersonDialog({ open, onOpenChange, endpoint, kind, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", brain_type: null, location: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const titles = {
    coach: { title: "Add Coach", desc: "Create a coach for this institute.", btn: "Add Coach", success: "Coach added" },
    user: { title: "Add Client", desc: "Create a client under this coach.", btn: "Add Client", success: "Client added" },
  };
  const t = titles[kind];

  const close = (v) => {
    if (!v) {
      setForm({ name: "", email: "", password: "", brain_type: null, location: "" });
      setError("");
    }
    onOpenChange(v);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.brain_type == null) delete payload.brain_type;
      // Only coaches carry a `location`; strip it for client creation
      if (kind !== "coach") delete payload.location;
      await api.post(endpoint, payload);
      toast.success(t.success);
      close(false);
      onCreated?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="bg-white border-[#E5E2DC] sm:max-w-lg"
        data-testid={`add-${kind}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold">
            {t.title}
          </DialogTitle>
          <DialogDescription className="text-[#5C635A]">{t.desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <Field
            label="Full Name"
            testid={`add-${kind}-name-input`}
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
          />
          <Field
            label="Email"
            type="email"
            testid={`add-${kind}-email-input`}
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            required
          />
          <Field
            label="Password"
            type="password"
            testid={`add-${kind}-password-input`}
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            required
          />
          <BrainTypeField
            value={form.brain_type}
            onChange={(v) => setForm({ ...form, brain_type: v })}
            testid={`add-${kind}-brain-type-input`}
            label="Brain Type (1–9, optional)"
          />
          {kind === "coach" && (
            <Field
              label="Location (optional)"
              testid={`add-${kind}-location-input`}
              value={form.location}
              onChange={(v) => setForm({ ...form, location: v })}
              placeholder="City, country — visible to prospects during registration"
            />
          )}
          {error && (
            <p
              className="text-sm text-[#9C6A53] bg-[#9C6A53]/10 border border-[#9C6A53]/20 rounded-lg px-4 py-3"
              data-testid={`add-${kind}-error`}
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="submit"
              disabled={saving}
              data-testid={`add-${kind}-submit-button`}
              className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
            >
              {saving ? "Saving..." : t.btn}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">{label}</Label>
      <Input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="h-11 rounded-lg bg-white border-[#E5E2DC] focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
      />
    </div>
  );
}
